import { useRef, useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle, Ref } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import Editor, { OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import CodeIcon from '@mui/icons-material/Code';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import { useGetPersonalFields } from '../../../../hooks/Marketing';
import InsertBarcodeDialog, { BarcodeResult } from './InsertBarcodeDialog';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import HtmlPreviewDialog from './HtmlPreviewDialog';

interface EmailUnlayerSectionProps {
  onSave?: (html: string, design: object) => void;
  isLandingPage?: boolean;
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
}

export interface EmailUnlayerHandle {
  getContent: () => Promise<{ mode: 'unlayer' | 'html'; html: string; design?: object } | null>;
}

const EmailUnlayerSection = forwardRef((props: EmailUnlayerSectionProps, ref: Ref<EmailUnlayerHandle>) => {
  const { onSave, isLandingPage = false, offerCount = 1, offerTitles, onAddOffer, onOfferUsed, onOfferIndicesChange } = props;
  const { sidebarColor } = useLayoutPreferences();
  const editorRef = useRef<EditorRef>(null);
  const [subject, setSubject] = useState('');
  const [saved, setSaved] = useState(false);
  const [ready, setReady] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [personalFieldsOpen, setPersonalFieldsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [editorMode, setEditorMode] = useState<'unlayer' | 'html'>('unlayer');
  const [htmlContent, setHtmlContent] = useState('');
  const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const htmlEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleHtmlEditorMount: OnMount = useCallback((editorInstance) => {
    htmlEditorRef.current = editorInstance;
  }, []);

  const insertAtCursorHtml = useCallback((text: string) => {
    const ed = htmlEditorRef.current;
    if (!ed) return;
    const model = ed.getModel();
    const pos = ed.getPosition();
    if (!pos) {
      const lineCount = model?.getLineCount() ?? 1;
      const lastCol = model?.getLineMaxColumn(lineCount) ?? 1;
      ed.executeEdits('insert', [{
        range: { startLineNumber: lineCount, startColumn: lastCol, endLineNumber: lineCount, endColumn: lastCol },
        text, forceMoveMarkers: true,
      }]);
    } else {
      ed.executeEdits('insert', [{
        range: { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: pos.lineNumber, endColumn: pos.column },
        text, forceMoveMarkers: true,
      }]);
    }
    ed.focus();
  }, []);

  const { data: personalFieldCategories } = useGetPersonalFields('EMAILING');

  const dynamicMergeTags = useMemo(() => {
    const tags: Record<string, { name: string; mergeTags: Record<string, { name: string; value: string }> }> = {};
    const offerMergeTags: Record<string, { name: string; value: string }> = {};
    Array.from({ length: Math.max(1, offerCount) }, (_, i) => {
      offerMergeTags[`offre_${i + 1}`] = { name: `Offre ${i + 1}`, value: `#NUM_COUPON_${i}#` };
    });
    offerMergeTags['num_carte'] = { name: 'Numéro de carte', value: '#NUM_CARTE#' };
    tags['codes_operation'] = { name: 'Codes opération', mergeTags: offerMergeTags };
    Object.entries(personalFieldCategories).forEach(([catName, fields]) => {
      const catKey = catName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      tags[catKey] = {
        name: catName,
        mergeTags: Object.fromEntries(
          fields.map(f => [f.ANCHOR.replace(/[^a-zA-Z0-9]/g, '_'), { name: f.NAME, value: f.ANCHOR }])
        ),
      };
    });
    return tags;
  }, [personalFieldCategories, offerCount]);

  const staticEditorOptions = useMemo(() => ({
    projectId: 219809,
    locale: 'fr-FR',
    appearance: { theme: 'light' as const },
    translations: { 'fr-FR': { 'labels.merge_tags': 'Codes & Champs' } },
  }), []);

  useEffect(() => {
    if (!ready) return;
    (editorRef.current?.editor as any)?.setMergeTags?.(dynamicMergeTags);
  }, [ready, dynamicMergeTags]);

  const insertIntoUnlayer = (content: string, type: 'text' | 'image', imageUrl?: string) => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    editor.exportHtml(({ design }) => {
      const id = Date.now();

      const blockContent = type === 'image' && imageUrl
        ? {
            id: `cnt_${id}`,
            type: 'image',
            values: {
              src: { url: imageUrl, width: 220, height: 70, autoWidth: false, maxWidth: '100%' },
              altText: content,
              action: { name: 'web', values: { href: '', target: '_blank' } },
              containerPadding: '10px',
              selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
            },
          }
        : {
            id: `cnt_${id}`,
            type: 'text',
            values: {
              text: `<p style="font-size:14px;line-height:140%;">${content}</p>`,
              containerPadding: '10px',
              selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
            },
          };

      const newRow = {
        id: `row_${id}`,
        cells: [1],
        columns: [{
          id: `col_${id}`,
          contents: [blockContent],
          values: {},
        }],
        values: {
          displayCondition: null,
          columns: false,
          backgroundColor: '',
          columnsBackgroundColor: '',
          padding: '0px',
          selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
        },
      };

      const updatedDesign = {
        ...design,
        body: {
          ...design.body,
          rows: [...((design as any).body?.rows ?? []), newRow],
        },
      };

      editor.loadDesign(updatedDesign as never);
      setSnackbar({
        open: true,
        message: type === 'image'
          ? `Bloc image ajouté dans l'email (${content})`
          : `Bloc texte ajouté dans l'email`,
      });
    });
  };

  const handleInsertPersonalField = (anchor: string) => {
    if (editorMode === 'html') {
      insertAtCursorHtml(anchor);
      return;
    }
    insertIntoUnlayer(anchor, 'text');
  };

  const handleBarcodeConfirm = (result: BarcodeResult) => {
    if (editorMode === 'html') {
      insertAtCursorHtml(result.snippet);
    } else if (result.isImage && result.imageUrl) {
      insertIntoUnlayer(result.token, 'image', result.imageUrl);
    } else {
      insertIntoUnlayer(result.token, 'text');
    }
    const match = result.token.match(/#NUM_COUPON_(\d+)#/);
    if (match) {
      const idx = parseInt(match[1]);
      onOfferUsed?.(idx);
      if (idx >= offerCount) onAddOffer?.();
    }
  };

  const onOfferIndicesChangeRef = useRef(onOfferIndicesChange);
  onOfferIndicesChangeRef.current = onOfferIndicesChange;

  const handleReady = useCallback(() => {
    setReady(true);
    const editor = editorRef.current?.editor as any;
    if (!editor) return;
    editor.addEventListener('design:updated', () => {
      (editorRef.current?.editor as any)?.exportHtml(({ html }: { html: string }) => {
        const indices = new Set<number>();
        const re = /#NUM_COUPON_(\d+)#/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(html)) !== null) indices.add(parseInt(m[1]));
        onOfferIndicesChangeRef.current?.(indices);
      });
    });
  }, []);

  const handleSave = () => {
    editorRef.current?.editor?.exportHtml(({ html, design }) => {
      onSave?.(html, design);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    });
  };

  useImperativeHandle(ref, () => ({
    getContent: () => new Promise(resolve => {
      if (editorMode === 'html') {
        resolve({ mode: 'html', html: htmlContent });
        return;
      }
      const editor = editorRef.current?.editor;
      if (!editor) {
        resolve(null);
        return;
      }
      editor.exportHtml(({ html, design }) => {
        resolve({ mode: 'unlayer', html, design });
      });
    }),
  }), [editorMode, htmlContent]);

  const handleShowPreview = () => {
    if (editorMode === 'html') {
      setPreviewHtml(htmlContent);
      setHtmlPreviewOpen(true);
      return;
    }
    const editor = editorRef.current?.editor;
    if (!editor) return;
    editor.exportHtml(({ html }) => {
      setPreviewHtml(html);
      setHtmlPreviewOpen(true);
    });
  };

  const switchToHtml = () => {
    setEditorMode('html');
  };

  const switchToUnlayer = () => {
    setEditorMode('unlayer');
  };

  const filledBtnSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.82rem',
    backgroundColor: sidebarColor,
    color: '#fff',
    borderRadius: 1.5,
    '&:hover': { backgroundColor: sidebarColor, opacity: 0.88 },
  };

  const outlineBtnSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.82rem',
    borderColor: sidebarColor,
    color: sidebarColor,
    borderRadius: 1.5,
    '&:hover': { borderColor: sidebarColor, backgroundColor: `${sidebarColor}10` },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', p: 1.5, mb: 2, border: '1px solid #e9ecef', borderRadius: 2, backgroundColor: '#f8f9fa' }}>
        <Button variant="contained" size="small" startIcon={<QrCode2Icon fontSize="small" />}
          onClick={() => setBarcodeOpen(true)} sx={filledBtnSx} disabled={editorMode === 'html' ? false : !ready}>
          Code opération
        </Button>
        <Button variant="contained" size="small" startIcon={<PersonAddAltIcon fontSize="small" />}
          onClick={() => setPersonalFieldsOpen(true)} sx={filledBtnSx} disabled={editorMode === 'html' ? false : !ready}>
          Champ de personnalisation
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button variant="outlined" size="small" startIcon={<VisibilityIcon fontSize="small" />}
          onClick={handleShowPreview} disabled={editorMode === 'html' ? !htmlContent.trim() : !ready}
          sx={outlineBtnSx}>
          Prévisualiser
        </Button>
        <Button variant="contained" size="small"
          startIcon={saved ? <CheckCircleOutlineIcon fontSize="small" /> : <SaveIcon fontSize="small" />}
          onClick={handleSave} disabled={!ready || editorMode === 'html'}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', backgroundColor: saved ? '#28a745' : sidebarColor, '&:hover': { backgroundColor: saved ? '#28a745' : sidebarColor, opacity: 0.9 } }}>
          {saved ? 'Sauvegardé !' : 'Sauvegarder'}
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button
          variant="outlined"
          size="small"
          startIcon={editorMode === 'unlayer' ? <CodeIcon fontSize="small" /> : <DashboardCustomizeIcon fontSize="small" />}
          onClick={editorMode === 'unlayer' ? switchToHtml : switchToUnlayer}
          disabled={!ready}
          sx={{ ...outlineBtnSx, fontWeight: 600 }}
        >
          {editorMode === 'unlayer' ? 'Vue HTML' : 'Builder visuel'}
        </Button>
      </Box>

      {!isLandingPage && (
        <>
          <TextField
            label={`Objet (${subject.length}/75 caractères)`}
            value={subject}
            onChange={e => setSubject(e.target.value)}
            inputProps={{ maxLength: 75 }}
            fullWidth size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sidebarColor }, '& .MuiInputLabel-root.Mui-focused': { color: sidebarColor } }}
          />
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {editorMode === 'html' && (
        <Box sx={{ position: 'relative', border: '1px solid #dee2e6', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#1e1e1e', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ fontSize: '0.9rem', color: '#9cdcfe' }} />
            <Typography variant="caption" sx={{ color: '#9cdcfe', fontFamily: 'monospace', fontSize: '0.75rem' }}>
              HTML
            </Typography>
            <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.7rem', ml: 'auto' }}>
              Modifiez le HTML puis cliquez sur "Builder visuel" pour revenir dans Unlayer
            </Typography>
          </Box>
          <Editor
            height="600px"
            language="html"
            value={htmlContent}
            onChange={val => setHtmlContent(val ?? '')}
            onMount={handleHtmlEditorMount}
            theme="vs-dark"
            options={{
              fontSize: 13,
              lineNumbers: 'on',
              minimap: { enabled: false },
              wordWrap: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              formatOnPaste: true,
              formatOnType: true,
              folding: true,
              padding: { top: 10, bottom: 8 },
              renderLineHighlight: 'all',
              cursorBlinking: 'smooth',
            }}
          />
        </Box>
      )}

      <Box sx={{ display: editorMode === 'unlayer' ? 'block' : 'none', position: 'relative', minHeight: 600, border: '1px solid #dee2e6', borderRadius: 2, overflow: 'hidden' }}>
        {!ready && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, zIndex: 1, backgroundColor: '#f8f9fa' }}>
            <CircularProgress size={32} sx={{ color: sidebarColor }} />
            <Typography variant="caption" color="text.secondary">
              Chargement de l'éditeur…
            </Typography>
          </Box>
        )}
        <EmailEditor
          ref={editorRef}
          onReady={handleReady}
          options={staticEditorOptions}
          style={{ height: 600, opacity: ready ? 1 : 0 }}
        />
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ fontSize: '0.82rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <InsertBarcodeDialog
        open={barcodeOpen}
        onClose={() => setBarcodeOpen(false)}
        onConfirm={handleBarcodeConfirm}
        offerCount={offerCount}
        offerTitles={offerTitles}
      />
      <InsertPersonalFieldsDialog
        open={personalFieldsOpen}
        onClose={() => setPersonalFieldsOpen(false)}
        onInsert={handleInsertPersonalField}
        channel="email"
      />
      <HtmlPreviewDialog
        open={htmlPreviewOpen}
        onClose={() => setHtmlPreviewOpen(false)}
        htmlContent={previewHtml}
        defaultDevice="desktop"
      />
    </Box>
  );
});

EmailUnlayerSection.displayName = 'EmailUnlayerSection';

export default EmailUnlayerSection;
